import { useMemo } from 'react';
import { useGetAllDettesQuery } from 'actions/dettes/queries';
import { useUpdateDetteMutation } from 'actions/dettes/mutations';
import { useGetEcrituresManuellesQuery } from 'actions/comptabilite/queries';
import { useGetFournisseurQuery } from 'actions/fournisseurs/queries';
import { toast } from 'react-toastify';

const useDettesData = (dossierId) => {
  const { data: dettesResponse, isLoading, isFetching, refetch } = useGetAllDettesQuery({
    dossier_id: dossierId,
  });
  const { data: ecrituresResponse, refetch: refetchEcritures } = useGetEcrituresManuellesQuery(
    { dossier_id: dossierId },
    { skip: !dossierId }
  );
  const { data: fournisseurResponse } = useGetFournisseurQuery({ dossier_id: dossierId }, { skip: !dossierId });

  const [updateDette] = useUpdateDetteMutation();

  const { updatedDettes, count, totalDettes } = useMemo(() => {
    if (!dettesResponse) {
      return { updatedDettes: [], count: 0, totalDettes: 0 };
    }

    const dettesData = Array.isArray(dettesResponse)
      ? dettesResponse
      : dettesResponse?.data;

    // Map compte_comptable -> nom fournisseur
    const fournisseurData = Array.isArray(fournisseurResponse?.data) ? fournisseurResponse.data : (Array.isArray(fournisseurResponse) ? fournisseurResponse : []);
    const fournisseurByCompte = {};
    fournisseurData.forEach((f) => {
      if (f.compte_comptable) fournisseurByCompte[String(f.compte_comptable)] = f;
    });

    // ── Écritures manuelles avec crédit 401xxx → dettes fournisseur ──
    const ecritures = ecrituresResponse?.ecritures || [];
    const ecrituresAsDettes = ecritures
      .filter((e) => {
        if (e.sortie_source || e.entree_source) return false;
        return String(e.code_credit || "").startsWith("401");
      })
      .map((e) => {
        const codeCredit = String(e.code_credit || "");
        const fournisseur = fournisseurByCompte[codeCredit];
        const montant = Number(e.credit || 0);
        const fNom = fournisseur ? fournisseur.nom : (codeCredit.startsWith("401") ? "Fournisseur (compte " + codeCredit + ")" : "Non spécifié");
        return {
          id: `MAN-${e.id}`,
          _source: "manuelle",
          intitule: e.libelle_original || e.libelle || "Dette (saisie manuelle)",
          fournisseurs_details: { nom: fNom },
          fournisseur_nom: fNom,
          prix: montant,
          montant_total: montant,
          montant_paye: 0,
          montant_restant: montant,
          reste_a_payer: montant,
          date_transaction: e.date || null,
          updated_at: e.date || null,
          archived: false,
          statut: e.workflow_status === "VALIDE" ? "VALIDE" : "BROUILLON",
          statut_paiement: "NON_PAYE",
        };
      });

    if (dettesData && Array.isArray(dettesData)) {
      const allDettes = [...dettesData, ...ecrituresAsDettes];
      allDettes.sort((a, b) => {
        const dateA = new Date(a.date_transaction || a.created_at || a.updated_at || 0).getTime();
        const dateB = new Date(b.date_transaction || b.created_at || b.updated_at || 0).getTime();
        if (dateB !== dateA) return dateB - dateA;
        const idA = typeof a.id === 'number' ? a.id : parseInt(String(a.id).replace(/\D/g, '') || '0', 10);
        const idB = typeof b.id === 'number' ? b.id : parseInt(String(b.id).replace(/\D/g, '') || '0', 10);
        return idB - idA;
      });
      return {
        updatedDettes: allDettes,
        count: (dettesResponse?.count || dettesData.length) + ecrituresAsDettes.length,
        totalDettes: (dettesResponse?.totalDettes || 0) + ecrituresAsDettes.reduce((s, e) => s + (e.montant_total || 0), 0),
      };
    }

    return { updatedDettes: ecrituresAsDettes, count: ecrituresAsDettes.length, totalDettes: 0 };
  }, [dettesResponse, ecrituresResponse, fournisseurResponse]);

  const handleUpdateDette = async (detteId, updateData) => {
    try {
      await updateDette({ id: detteId, ...updateData }).unwrap();
      refetch();
    } catch (error) {
      toast.error(
        `Erreur lors de la mise à jour: ${error.data?.error || error.message}`
      );
      console.error(error);
    }
  };

  return {
    updatedDettes,
    handleUpdateDette,
    count,
    totalDettes,
    isLoading: isLoading || (isFetching && !dettesResponse),
    refetch: () => { refetch(); refetchEcritures(); },
  };
};

export default useDettesData;

